import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ExamsService } from '../exams.service';
import { PatientsService } from '../patients.service';
import { PregnancyService } from '../pregnancy.service';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { tap, switchMap, takeUntil, finalize } from 'rxjs/operators';
import { LoginService } from '../login.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CommerrorService } from '../commerror.service';
import { ConsentTypeDef, resolveConsentType } from '../consent-types';

@Component({
    selector: 'app-usg-consent-form',
    templateUrl: './usg-consent-form.component.html',
    styleUrls: ['./usg-consent-form.component.scss'],
    standalone: false
})
export class UsgConsentFormComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<string>();
  @ViewChild('canvas', { static: false }) canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasDoc', { static: false }) canvasDoc: ElementRef<HTMLCanvasElement>;
  @Input('form') form;
  @Input('consentType') consentType: string;
  @Input('pat') pat;

  ctx: CanvasRenderingContext2D;
  concentForm: FormGroup;
  typecanvas;
  changeCanvas$ = new Subject();
  destroy$ = new Subject();
  sub = new Subscription();
  type;
  lockedConsent: boolean = false;
  showlink: boolean = false;
  waitingForSignatures: boolean = false;
  errorInSignature: boolean = false;
  signatureSubscription: Subscription;

  constructor(
    private changeDetector: ChangeDetectorRef,
    public ce: CommerrorService,
    public http: HttpClient,
    private fb: FormBuilder,
    public ls: LoginService,
    public es: ExamsService,
    public patS: PatientsService,
    public ps: PregnancyService
  ) {
    this.concentForm = this.fb.group({
      AutoIndex: [''],
      PID: [''],
      UID: [''],
      GID: [''],
      PregID: [''],
      ExamID: [''],
      Opener: [''],
      Deleted: [''],
      DeletedBy: [''],
      ID: [''],
      First: [''],
      Last: [''],
      Phone: [''],
      CreationTime: [''],
      refferalDoc: [''],
      ConsentType: [''],
      patSign: [''],
      patSignApproved: [''],
      docSign: [''],
      printed: [''],
    });
  }

  get consentDef(): ConsentTypeDef {
    const type = this.concentForm?.get('ConsentType')?.value || this.consentType;
    return resolveConsentType(type);
  }

  async ngOnInit() {
    this.resetForm();
    this.sub = this.ce.saveConcent$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) {
        this.save();
        if (
          this.concentForm.get('ExamID').value == '' &&
          this.concentForm.get('AutoIndex').value == ''
        ) {
          this.closed.emit('1');
        } else if (
          this.concentForm.get('ExamID').value &&
          this.concentForm.get('ExamID').value != '' &&
          this.concentForm.get('AutoIndex').value != ''
        ) {
          this.closed.emit('1');
        } else {
          this.closed.emit('2');
        }
      } else {
        this.closed.emit('1');
      }
      this.form = null;
      this.concentForm.reset();
      this.sub.unsubscribe();
    });

    const isSigned = this.checkApprovedConcent(this.concentForm.value)
      || !!this.concentForm.get('printed').value;

    if (isSigned) {
      this.concentForm.disable({ emitEvent: false });
      this.lockedConsent = true;
      return;
    }

    if (this.ls.userType === 'secretary') {
      this.concentForm.get('refferalDoc').setValue(this.es.consentPractitioner);
      this.concentForm.get('UID').setValue(this.es.consentDocUID);
      (await this.es.getPrevDocSign()).subscribe((data: any) => {
        this.concentForm.get('docSign').setValue(data?.docSign);
        this.loadSign('docSign');
      });
    } else if (this.ls.userType === 'doctor') {
      this.concentForm.get('refferalDoc').setValue(this.ls.username);
      this.concentForm.get('UID').setValue(this.ls.UID);
      (await this.es.getPrevDocSign()).subscribe((data: any) => {
        this.concentForm.get('docSign').setValue(data?.docSign);
        this.loadSign('docSign');
      });
    }
  }

  async save() {
    const form = this.concentForm.getRawValue();
    if (this.es.examID) form.ExamID = this.es.examID;
    if (this.ls.userType === 'secretary') {
      form.UID = this.es.consentDocUID;
    }
    const data: any = await this.http
      .post('/api/consent/usg-save', {
        form,
        pat: this.patS.getPat(),
      })
      .toPromise();
    this.es.concentForm = data;
    if (data?.AutoIndex) {
      this.concentForm.get('AutoIndex').setValue(data.AutoIndex, { emitEvent: false });
    }
    return data;
  }

  resetForm() {
    ['First', 'Last', 'ID', 'Phone', 'refferalDoc', 'CreationTime'].forEach(f =>
      this.concentForm.get(f)?.disable({ emitEvent: false })
    );
    if (this.form) {
      if (this.es.examID) this.form['ExamID'] = this.es.examID;
      if (this.form.CreationTime) {
        const d = new Date(this.form.CreationTime);
        const pad = (n: number) => n.toString().padStart(2, '0');
        this.form.CreationTime =
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
          `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      }
      this.concentForm.patchValue(this.form, { emitEvent: false });
      this.es.concentForm = this.form;
    } else {
      const consentType = this.consentType || this.es.consentType || '';
      this.concentForm.get('ConsentType').setValue(consentType);
      const pat = this.pat || this.patS.getPat();
      if (pat?.Last) this.concentForm.get('Last').setValue(pat.Last);
      if (pat?.First) this.concentForm.get('First').setValue(pat.First);
      if (pat?.ID) this.concentForm.get('ID').setValue(pat.ID);
      if (pat?.Phone) this.concentForm.get('Phone').setValue(pat.Phone);
      if (pat?.Mobile) this.concentForm.get('Phone').setValue(pat.Mobile);
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      this.concentForm.get('CreationTime').setValue(
        `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
        `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
      );
    }
  }

  ngAfterViewInit() {
    this.changeDetector.detectChanges();
    this.initCanvasStream(this.canvas, 'patSign');
    this.initCanvasStream(this.canvasDoc, 'docSign');
    if (this.form?.patSign) this.loadSign('patSign');
    if (this.form?.docSign) this.loadSign('docSign');
  }

  private initCanvasStream(canvasRef: ElementRef<HTMLCanvasElement>, field: string) {
    if (!canvasRef) return;
    const el = canvasRef.nativeElement;
    const ctx = el.getContext('2d');
    const down$ = fromEvent<MouseEvent>(el, 'mousedown');
    const move$ = fromEvent<MouseEvent>(el, 'mousemove');
    const up$ = fromEvent(window, 'mouseup');
    down$.pipe(
      tap(e => {
        if (this.lockedConsent) return;
        ctx.beginPath();
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 5;
        ctx.lineJoin = 'round';
        ctx.moveTo(e.offsetX, e.offsetY);
      }),
      switchMap(() => move$.pipe(
        tap(e => {
          if (this.lockedConsent) return;
          ctx.lineTo(e.offsetX, e.offsetY);
          ctx.stroke();
        }),
        takeUntil(up$),
        finalize(() => {
          if (!this.lockedConsent)
            this.concentForm.get(field)?.setValue(el.toDataURL('image/png'));
        })
      )),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  close(toclose: boolean) {
    if (toclose) {
      this.ce.errorCalendar('Save Consent Form?');
    } else {
      this.closed.emit('1');
    }
  }

  loadSign(type: string) {
    let canvasEl: HTMLCanvasElement;
    switch (type) {
      case 'patSign': canvasEl = this.canvas?.nativeElement; break;
      case 'docSign': canvasEl = this.canvasDoc?.nativeElement; break;
      default: return;
    }
    if (!canvasEl) return;
    const ctx = canvasEl.getContext('2d');
    const img = document.createElement('img');
    img.onload = () => ctx.drawImage(img, 0, 0, canvasEl.width, canvasEl.height);
    img.src = this.concentForm.get(type).value;
  }

  clear(type: string) {
    let canvasEl: HTMLCanvasElement;
    switch (type) {
      case 'patSign': canvasEl = this.canvas?.nativeElement; break;
      case 'docSign': canvasEl = this.canvasDoc?.nativeElement; break;
      default: return;
    }
    if (!canvasEl) return;
    canvasEl.getContext('2d').clearRect(0, 0, canvasEl.width, canvasEl.height);
    this.concentForm.get(type).setValue('');
  }

  async printConsent() {
    let autoIndex = this.concentForm.get('AutoIndex').value;
    if (!autoIndex) {
      const data: any = await this.save();
      autoIndex = data?.AutoIndex;
    }
    this.closed.emit('1');
    this.es.markUsgConsentPrinted(autoIndex).subscribe();
    window.open(`api/consent/print-usg/${autoIndex}`);
  }

  toggleSave(): boolean {
    if (!this.concentForm.get('docSign').value) return true;
    if (!this.concentForm.get('patSign').value) return true;
    return false;
  }

  async handleSaveAndShowLink() {
    await this.save();
    this.es.consentSaved$.next();
    this.showlink = true;
  }

  closeSendLinks(openSocket: boolean) {
    this.showlink = false;
    if (openSocket) {
      this.waitingForSignatures = true;
      this.patS.openConsentSocket(this.es.concentForm?.PID);
      if (!this.signatureSubscription) {
        this.signatureSubscription = this.patS.signatureIncoming.subscribe(() => {
          this.es.getUpdatedUsgConsent().subscribe((concent: any) => {
            this.es.concentForm = concent;
            this.waitingForSignatures = false;
            const signed = this.checkApprovedConcent(this.es.concentForm);
            if (signed) {
              this.closed.emit('1');
              this.errorInSignature = false;
            } else {
              this.errorInSignature = true;
              this.waitingForSignatures = false;
            }
          });
        });
      }
    }
  }

  closeFromLinks(toclose: boolean) {
    if (toclose) {
      this.closed.emit('1');
    }
  }

  checkApprovedConcent(consent: any): boolean {
    return !!(consent?.docSign && consent?.patSign);
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.sub.unsubscribe();
    if (this.signatureSubscription) {
      this.signatureSubscription.unsubscribe();
    }
  }
}
